import React, { useEffect, useState } from "react";
import axios from "axios";
import { API_BASE_URL } from "../config/api";
import {
  applyThemePreference,
  getStoredThemePreference,
  THEME_OPTIONS
} from "../utils/theme";

export default function AdminProfile() {
  const [loading, setLoading] = useState(true);
  // eslint-disable-next-line no-unused-vars
  const [profile, setProfile] = useState(null);
  const [profileForm, setProfileForm] = useState({ name: "", email: "", phone: "+91 98765 43210" });
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: "",
    newPassword: ""
  });
  const [themePreference, setThemePreference] = useState(() => getStoredThemePreference());
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(false);

  useEffect(() => {
    loadProfile();
  }, []);

  const loadProfile = async () => {
    try {
      setLoading(true);
      const res = await axios.get(API_BASE_URL + "/api/admin/profile", {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` }
      });
      setProfile(res.data.admin || null);
      setProfileForm({
        name: res.data.admin?.name || "",
        email: res.data.admin?.email || "",
        phone: "+91 98765 43210"
      });
    } catch (error) {
      console.error(error);
      alert("Failed to load profile details.");
    } finally {
      setLoading(false);
    }
  };

  const updateThemePreference = (value) => {
    setThemePreference(value);
    applyThemePreference(value);
  };

  const saveProfile = async (e) => {
    e.preventDefault();
    try {
      const res = await axios.put(API_BASE_URL + "/api/admin/profile", profileForm, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` }
      });
      setProfile(res.data);
      localStorage.setItem("adminName", res.data.name || "");
      alert("Personal profile updated successfully!");
    } catch (error) {
      alert("Failed to update profile details.");
    }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    try {
      await axios.put(API_BASE_URL + "/api/admin/profile/password", passwordForm, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` }
      });
      setPasswordForm({ currentPassword: "", newPassword: "" });
      alert("Password updated successfully!");
    } catch (error) {
      alert(error.response?.data?.message || "Failed to update password.");
    }
  };

  if (loading) {
    return <div className="text-center py-12 text-slate-500 font-semibold">Loading profile...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="border-b pb-4">
        <h1 className="text-2xl font-extrabold text-slate-900 dark:text-white">My Profile</h1>
        <p className="text-sm text-slate-500 mt-1">Manage your personal details, credentials, and settings preferences.</p>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        
        {/* Profile Card & Info */}
        <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 p-6 shadow-sm flex flex-col items-center text-center space-y-4">
          <div className="relative">
            <div className="w-24 h-24 rounded-full bg-blue-600 flex items-center justify-center text-white text-3xl font-extrabold shadow-md border-4 border-slate-50 dark:border-slate-750">
              {profileForm.name.charAt(0).toUpperCase()}
            </div>
            <button className="absolute bottom-0 right-0 p-1.5 bg-slate-900 hover:bg-black text-white rounded-full shadow-sm transition">
              <span className="material-symbols-outlined text-xs block">photo_camera</span>
            </button>
          </div>

          <div>
            <h2 className="text-lg font-bold text-slate-900 dark:text-white">{profileForm.name}</h2>
            <p className="text-xs text-blue-650 dark:text-blue-400 font-semibold mt-0.5">Admin Account</p>
          </div>

          <div className="w-full text-left text-xs space-y-2 border-t pt-4 text-slate-600 dark:text-slate-400">
            <div className="flex justify-between"><span className="font-bold">Employee ID:</span> <span>EMP-ADM-01</span></div>
            <div className="flex justify-between"><span className="font-bold">Department:</span> <span>Administration</span></div>
            <div className="flex justify-between"><span className="font-bold">Role:</span> <span>Super Admin</span></div>
            <div className="flex justify-between"><span className="font-bold">Registered Email:</span> <span className="truncate max-w-[150px]">{profileForm.email}</span></div>
          </div>
        </div>

        {/* Edit Details */}
        <form onSubmit={saveProfile} className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 p-6 shadow-sm space-y-4">
          <h2 className="text-lg font-bold text-slate-850 dark:text-white border-b pb-2 flex items-center gap-2">
            <span className="material-symbols-outlined text-blue-600 dark:text-blue-400">badge</span>
            Personal Details
          </h2>

          <div className="flex flex-col gap-1">
            <label className="text-xs font-bold text-slate-650 dark:text-slate-350">Display Name</label>
            <input
              type="text"
              required
              value={profileForm.name}
              onChange={(e) => setProfileForm(p => ({ ...p, name: e.target.value }))}
              className="w-full border border-slate-250 dark:border-slate-700 bg-white dark:bg-slate-900 rounded-xl px-4 py-2.5 text-sm outline-none transition focus:border-blue-400"
            />
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-xs font-bold text-slate-650 dark:text-slate-350">Email Address</label>
            <input
              type="email"
              required
              value={profileForm.email}
              onChange={(e) => setProfileForm(p => ({ ...p, email: e.target.value }))}
              className="w-full border border-slate-250 dark:border-slate-700 bg-white dark:bg-slate-900 rounded-xl px-4 py-2.5 text-sm outline-none transition focus:border-blue-400"
            />
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-xs font-bold text-slate-650 dark:text-slate-350">Phone Number</label>
            <input
              type="text"
              value={profileForm.phone}
              onChange={(e) => setProfileForm(p => ({ ...p, phone: e.target.value }))}
              className="w-full border border-slate-250 dark:border-slate-700 bg-white dark:bg-slate-900 rounded-xl px-4 py-2.5 text-sm outline-none transition focus:border-blue-400"
            />
          </div>

          <button className="bg-blue-600 hover:bg-blue-700 text-white font-semibold text-sm px-5 py-2.5 rounded-xl transition shadow">
            Save Details
          </button>
        </form>

        {/* Change Password */}
        <form onSubmit={changePassword} className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 p-6 shadow-sm space-y-4">
          <h2 className="text-lg font-bold text-slate-850 dark:text-white border-b pb-2 flex items-center gap-2">
            <span className="material-symbols-outlined text-orange-600 dark:text-orange-400">key</span>
            Update Password
          </h2>

          <div className="flex flex-col gap-1">
            <label className="text-xs font-bold text-slate-650 dark:text-slate-350">Current Password</label>
            <input
              type="password"
              required
              value={passwordForm.currentPassword}
              onChange={(e) => setPasswordForm(p => ({ ...p, currentPassword: e.target.value }))}
              className="w-full border border-slate-250 dark:border-slate-700 bg-white dark:bg-slate-900 rounded-xl px-4 py-2.5 text-sm outline-none transition focus:border-blue-400"
            />
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-xs font-bold text-slate-650 dark:text-slate-350">New Password</label>
            <input
              type="password"
              required
              value={passwordForm.newPassword}
              onChange={(e) => setPasswordForm(p => ({ ...p, newPassword: e.target.value }))}
              className="w-full border border-slate-250 dark:border-slate-700 bg-white dark:bg-slate-900 rounded-xl px-4 py-2.5 text-sm outline-none transition focus:border-blue-400"
            />
          </div>

          <button className="bg-slate-900 hover:bg-black text-white font-semibold text-sm px-5 py-2.5 rounded-xl transition shadow">
            Update Password
          </button>
        </form>

      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        {/* Appearance Preference */}
        <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 p-6 shadow-sm space-y-4">
          <h2 className="text-lg font-bold text-slate-850 dark:text-white border-b pb-2 flex items-center gap-2">
            <span className="material-symbols-outlined text-purple-650 dark:text-purple-400">palette</span>
            Appearance Theme
          </h2>
          <div className="space-y-2">
            {THEME_OPTIONS.map((option) => {
              const active = themePreference === option.value;
              return (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => updateThemePreference(option.value)}
                  className={`flex w-full items-center justify-between rounded-xl border px-4 py-3 text-left text-sm transition ${
                    active
                      ? "border-blue-600 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400 font-semibold"
                      : "border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-750"
                  }`}
                >
                  <span>{option.label}</span>
                  <span className="text-[10px] uppercase font-bold tracking-wider">{active ? "Selected" : "Choose"}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Security & 2FA */}
        <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 p-6 shadow-sm space-y-4">
          <h2 className="text-lg font-bold text-slate-850 dark:text-white border-b pb-2 flex items-center gap-2">
            <span className="material-symbols-outlined text-emerald-600 dark:text-emerald-400">security</span>
            Security &amp; 2FA
          </h2>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold text-slate-800 dark:text-slate-350">Two-Factor Authentication</p>
                <p className="text-xs text-slate-500">Add an extra layer of login protection.</p>
              </div>
              <button
                type="button"
                onClick={() => setTwoFactorEnabled(!twoFactorEnabled)}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold shadow-sm transition ${
                  twoFactorEnabled ? "bg-red-50 text-red-600 border border-red-200" : "bg-emerald-50 text-emerald-600 border border-emerald-250"
                }`}
              >
                {twoFactorEnabled ? "Disable" : "Enable"}
              </button>
            </div>
            <div className="border-t pt-4">
              <p className="text-xs font-bold text-slate-650 dark:text-slate-400 mb-2">Browser Login History</p>
              <div className="text-[11px] text-slate-500 space-y-1.5">
                <p>• Chrome (Windows) - India (Active Now)</p>
                <p>• Firefox (Windows) - India (2 hours ago)</p>
              </div>
            </div>
          </div>
        </div>

        {/* Notification preferences */}
        <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 p-6 shadow-sm space-y-4">
          <h2 className="text-lg font-bold text-slate-850 dark:text-white border-b pb-2 flex items-center gap-2">
            <span className="material-symbols-outlined text-amber-600 dark:text-amber-400">notifications</span>
            Notification Alerts
          </h2>
          <div className="space-y-3">
            <label className="flex items-center gap-3 cursor-pointer">
              <input type="checkbox" defaultChecked className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500" />
              <span className="text-xs font-semibold text-slate-700 dark:text-slate-300">Email updates for new complaints</span>
            </label>
            <label className="flex items-center gap-3 cursor-pointer">
              <input type="checkbox" defaultChecked className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500" />
              <span className="text-xs font-semibold text-slate-700 dark:text-slate-300">Daily Updates notification summaries</span>
            </label>
            <label className="flex items-center gap-3 cursor-pointer">
              <input type="checkbox" className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500" />
              <span className="text-xs font-semibold text-slate-700 dark:text-slate-300">Marketing &amp; platform newsletter alerts</span>
            </label>
          </div>
        </div>
      </div>
    </div>
  );
}
